<template>
  <v-toolbar flat color="white">
    <v-menu offset-y :close-on-content-click="false">
      <template v-slot:activator="{ on, attrs }">
        <v-btn small text outlined v-on="on" :attrs="attrs" color="primary"
          ><v-icon>mdi-filter</v-icon>Filters</v-btn
        >
      </template>
      <v-card max-width="1000px" style="overflow-y: auto">
        <v-card-text>
          <v-row dense>
            <v-col
              v-for="(item, index) in filtersList"
              :key="index"
              cols="12"
              sm="4"
            >
              <v-autocomplete
                v-if="!item.isDateField && !item.isToggle"
                :items="item.options"
                :value="
                  Array.isArray(item.value)
                    ? item.value.map((x) => x)
                    : item.value.id
                "
                item-value="id"
                @change="applyFilter(item, $event)"
                return-object
                clearable
                :multiple="item.allowMultiple"
                :label="item.label"
                hide-details
                dense
                outlined
                class="mx-1"
              ></v-autocomplete>
              <month-picker-field
                v-if="item.isDateField"
                :field="{ label: item.label }"
                :value="item.value.map((x) => x.id)"
                @fieldChange="applyDateFilter(item, $event)"
              ></month-picker-field>
              <v-switch
                v-if="item.isToggle"
                v-model="item.value"
                @change="applyFilter(item, $event)"
                x-small
                hide-details
                class="mx-1 mt-1"
              >
                <template v-slot:label>
                  <span class="text-body-2">{{ item.label }}</span></template
                ></v-switch
              >
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-menu>
    <v-btn small text @click="resetFilters">Reset</v-btn>
    <v-chip-group column>
      <template v-for="(filter, i) in appliedFilters">
        <v-chip
          :key="i"
          label
          close
          close-icon="mdi-close"
          @click:close="removeFilter(filter)"
          color="primary"
          dark
          small
          class="mx-1"
          >{{ filter.label }}: {{ filter.text }}</v-chip
        >
      </template>
    </v-chip-group>
  </v-toolbar>
</template>

<script>
import { formatShortDate } from "../../utils/format-utils";
import { ReportAssignmentStatus } from "../../objs/ReportAssignmentStatus";
import MonthPickerField from "../FormFields/month-picker-field.vue";
export default {
  components: { MonthPickerField },
  data() {
    return { statuses: Object.values(ReportAssignmentStatus) };
  },
  computed: {
    programView() {
      return this.$store.state.reportsModule.programView;
    },
    // options
    clientOptions() {
      return this.uniqueOptions(
        this.reportAssignments,
        (item) => item.clientId,
        (item) =>
          `${item.clientName} (${formatShortDate(item.clientDateOfBirth)})`
      ).sort((a, b) => a.text.localeCompare(b.text));
    },
    providerOptions() {
      return this.uniqueOptions(
        this.reportAssignments,
        (item) => item.providerId,
        (item) => item.providerName
      ).sort((a, b) => a.text.localeCompare(b.text));
    },
    teamOptions() {
      const teams = this.uniqueOptions(
        this.reportAssignments,
        ({ teamId }) => teamId,
        ({ teamName }) => teamName
      ).sort((a, b) => a.text.localeCompare(b.text));

      teams.push({ text: "No Team", id: "none" });
      return teams;
    },
    reportTypeOptions() {
      return this.$store.getters["optionsModule/reportTypes"](this.programView)
        .map((item) => {
          return {
            text: item.name,
            id: item.reportTypeId,
          };
        })
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    semesterOptions() {
      return (
        this.$store.state.optionsModule.semesters.filter(
          (x) => new Date(x.semesterStartDate) >= new Date(2023, 9, 1)
        ) || []
      )
        .map((item) => {
          return {
            text: item.semesterCode,
            id: item.semesterCode,
          };
        })
        .sort((a, b) => a - b);
    },
    // filters
    filtersList() {
      return [
        {
          label: "Client",
          name: "clientFilter",
          value: this.filters.clientFilter || "",
          options: this.clientOptions,
        },
        {
          label: "Provider",
          name: "providerFilter",
          value: this.filters.providerFilter || "",
          options: this.providerOptions,
        },
        {
          label: "Team",
          name: "teamFilter",
          show: this.programView === "SEIT",
          allowMultiple: true,
          value: this.filters.teamFilter || "",
          options: this.teamOptions,
        },
        {
          label: "EI Staffing Team",
          name: "eiStaffingTeamFilter",
          show: this.programView === "EIO",
          value: this.filters.eiStaffingTeamFilter || "",
          options: this.uniqueOptions(
            this.reportAssignments,
            (item) => item.eiStaffingTeam,
            (item) => item.eiStaffingTeam
          ),
        },
        {
          isDateField: true,
          label: "Due Date",
          allowMultiple: true,
          name: "dueDateFilter",
          value: this.filters.dueDateFilter.map((item) => item) || [],
        },
        {
          label: "Semester",
          name: "semesterFilter",
          show: this.programView === "SEIT",
          value: this.filters.semesterFilter || "",
          options: this.semesterOptions,
        },
        {
          label: "Quarter",
          name: "quarterFilter",
          show: this.programView === "SEIT",
          value: this.filters.quarterFilter || "",
          options: this.uniqueOptions(
            this.reportAssignments
              .slice()
              .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate)),
            (item) => item.quarterByYear,
            (item) => item.quarterByYear
          ),
        },
        {
          label: "Report Type",
          name: "reportTypeFilter",
          allowMultiple: true,
          value: this.filters.reportTypeFilter || "",
          options: this.reportTypeOptions,
        },
        {
          label: "Status",
          name: "statusFilter",
          allowMultiple: true,
          value: this.filters.statusFilter || "",
          options: this.statuses.map((item) => ({ id: item, text: item })),
        },
        {
          label: "Flag",
          name: "flagFilter",
          allowMultiple: true,
          value: this.filters.flagFilter || "",
          options: this.$store.state.optionsModule.flags.map((item) => ({
            id: item,
            text: item,
          })),
        },
        {
          label: "Service",
          name: "serviceFilter",
          show: this.programView === "EIO",
          allowMultiple: true,
          value: this.filters.serviceFilter || "",
          options: this.uniqueOptions(
            this.reportAssignments,
            (item) => item.service,
            (item) => item.service
          ).sort((a, b) => a.text.localeCompare(b.text)),
        },
        {
          label: "Year Of Birth",
          name: "yearOfBirthFilter",
          show: this.programView === "SEIT",
          allowMultiple: true,
          value: this.filters.yearOfBirthFilter || "",
          options: this.uniqueOptions(
            this.reportAssignments,
            (item) => item.yearOfBirth,
            (item) => `${item.yearOfBirth} DOB`
          ).sort((a, b) => a.id - b.id),
        },
        {
          label: "10/12 Month",
          name: "tenTwelveMonthFilter",
          show: this.programView === "SEIT",
          value: this.filters.tenTwelveMonthFilter || "",
          options: [
            { text: "10-month", id: "10" },
            { text: "12-month", id: "12" },
          ],
        },
        {
          isToggle: true,
          label: "Current Plans",
          show: this.programView === "SEIT",
          name: "currentPlansFilter",
          value: this.filters.currentPlansFilter,
        },
        {
          isToggle: true,
          label: "Active Assignments",
          show: this.programView === "SEIT",
          name: "activeAssignmentsFilter",
          value: this.filters.activeAssignmentsFilter,
        },
      ].filter((x) => x.show !== false);
    },
    reportAssignments() {
      return this.$store.state.reportsModule.reportAssignments || [];
    },
    filters() {
      return this.$store.state.reportsModule.filters;
    },
    appliedFilters() {
      return this.filtersList
        .filter(
          (item) =>
            item.value === true ||
            (Array.isArray(item.value) && item.value.length > 0) ||
            Object.keys(item.value).length > 0
        )
        .map((item) => {
          return {
            ...item,
            text: item.isToggle
              ? null
              : item.allowMultiple
              ? item.value.map((item) => item.text).join(",")
              : item.value.text,
          };
        });
    },
  },
  methods: {
    applyDateFilter(filter, value) {
      this.applyFilter(
        filter,
        value.map((item) => {
          return {
            id: item,
            text: `${item.toLocaleString("default", {
              month: "short",
            })} ${item.getFullYear()}`,
          };
        })
      );
    },
    applyFilter(filter, value) {
      if (value == null) {
        this.removeFilter(filter);
      } else {
        // save the value in the store
        this.$store.dispatch("reportsModule/setFilters", {
          filterName: filter.name,
          filterValue: value,
        });
      }
    },
    uniqueOptions(items, keySelector, textSelector) {
      const uniqueMap = new Map();

      for (const item of items) {
        const key = keySelector(item);
        if (key == null || uniqueMap.has(key)) continue;
        uniqueMap.set(key, {
          id: key,
          text: textSelector(item),
        });
      }

      return Array.from(uniqueMap.values());
    },
    removeFilter(filter) {
      if (filter.isDateField) {
        this.applyFilter(filter, []);
      } else if (filter.isToggle) {
        this.applyFilter(filter, false);
      } else {
        this.applyFilter(filter, {});
      }
    },
    resetFilters() {
      this.$store.dispatch("reportsModule/clearFilters");
      this.$store.dispatch("reportsModule/applyDefaultFilters");
    },
  },
};
</script>
